// rooms.js
//
// System Health → Active Rooms screen (plan Task 5.1). Atomic extraction of the
// ACTIVE ROOMS card from the old dashboard.js (`renderRooms`/`formatAge`) into
// its own screen. READ-ONLY: load + refresh, no mutation.
//
// Lifecycle: the Task 3.1 Step-3c idiom with ONE generation lane, `roomsGen`,
// shared by BOTH the initial load and every refresh — they write the SAME rooms
// table, so a late initial GET resolving after a completed refresh must never
// clobber it, and of two overlapping refreshes the OLDER one resolving last must
// be discarded. Each `load()` call bumps `roomsGen` on entry. A persistent
// `ctx.onTeardown` bumps `roomsGen` again on navigation and disposes the refresh
// click listener; each operation registers its OWN abort callback, unregistered
// in `finally` (self-unregistering — never accumulates). A successful response
// commit requires the CURRENT `roomsGen` token (checked before validating and
// rendering, plus a defensive `op.closed`/`op.signal.aborted` guard for a
// resolve landing exactly at the deadline). The terminal timeout/error
// presentation and the refresh button's baseline restore intentionally check
// ONLY the lane token (not `op.closed`, which a deadline abort has already
// flipped true) — so the CURRENT screen still recovers after a timeout, while a
// departed screen's bumped generation suppresses the same writes.
//
// Every response is validated with the shared `validateShape('/api/admin/rooms',
// data)` (apps/admin/src/lib/admin-validate.js) BEFORE any render — a malformed
// top-level/room-row/nested-player shape renders ONE structured section error,
// never a partial table. Rendered rows are capped at 100, with a "showing N of
// M" note when the response carries more than that.

import { api } from '../api.js';
import { createOperation, createGeneration, whenAborted, navAbort, abortErr } from '../lib/admin-lifecycle.js';
import { validateShape, toError } from '../lib/admin-validate.js';
import { escapeHtml } from '../../../../src/shared/escape-html.js';

const EP = '/api/admin/rooms';
export const ROW_CAP = 100;

// Originally moved from dashboard.js; now hardened with an invalid-input guard.
export function formatAge(ts) {
  const ms = Date.now() - (typeof ts === 'number' ? ts : new Date(ts).getTime());
  if (!Number.isFinite(ms)) return '–';   // en dash, never "NaNd NaNh" — defense in depth for a direct/unexpected call (validateShape now rejects unrenderable timestamps at the loader)
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
}

// A user-facing message keyed off the structured error's `kind` (see
// `toError`/`validateShape` in admin-validate.js) — distinguishable text per
// kind so a malformed-shape, timeout, network, or http failure each read
// differently (and are each independently assertable in tests).
function describeError(err) {
  if (!err) return 'Active rooms unavailable. Reload the page.';
  switch (err.kind) {
    case 'shape': return 'Active rooms unavailable: the /api/admin/rooms response was malformed. Reload the page.';
    case 'timeout': return "Couldn't load rooms: request timed out.";
    case 'cancelled': return 'Cancelled.';
    case 'network': return "Couldn't load rooms: network error.";
    case 'http': return `Couldn't load rooms: server error (${err.status}).`;
    default: return `Couldn't load rooms: ${err.cause?.message || 'unknown error'}.`;
  }
}

// One room row's cells. Moved verbatim from dashboard.js (escaping unchanged) —
// every interpolated value is either a trusted literal or passed through
// escapeHtml, so a hostile field can only ever land as inert text.
function roomRowHtml(r) {
  const playerNames = r.players.length
    ? r.players.map((p) => escapeHtml(p.username || 'guest')).join(', ')
    : '–';
  const typeBadge = `<span class="admin-room-type admin-room-type-${escapeHtml(r.type)}">${escapeHtml(r.type)}</span>`;
  const replicaShort = (r.replicaId || '').slice(0, 8);
  const replicaLabel = r.local
    ? `<span class="admin-room-local">${escapeHtml(replicaShort)}</span>`
    : escapeHtml(replicaShort);
  return (
    `<td class="admin-user-id">${escapeHtml(r.code)}</td>` +
    `<td>${escapeHtml(r.system || '–')}</td>` +
    `<td>${escapeHtml(r.romTitle || '–')}</td>` +
    `<td>${r.hostUsername ? `<span class="admin-user-username">${escapeHtml(r.hostUsername)}</span>` : '–'}</td>` +
    `<td>${playerNames}</td>` +
    `<td class="admin-user-num">${r.spectatorCount}</td>` +
    `<td>${typeBadge}</td>` +
    `<td class="admin-user-id">${replicaLabel}</td>` +
    `<td class="admin-user-date">${formatAge(r.createdAt)}</td>`
  );
}

export function renderRooms(params, ctx) {
  const root = document.querySelector('[data-screen="rooms"]');
  if (!root) return undefined;

  const el = (id) => root.querySelector(`#${id}`);
  const body = el('admin-rooms-body');
  const countEl = el('admin-rooms-count');
  const capNoteEl = el('admin-rooms-cap-note');
  const refreshBtn = el('admin-rooms-refresh');
  const statusEl = el('admin-rooms-status');
  if (!body) return undefined;

  // ONE lane shared by the initial load AND every refresh (Step 3c) — see the
  // header comment for why they must share a single generation.
  const roomsGen = createGeneration();
  const offs = [];
  const on = (target, ev, fn) => { target.addEventListener(ev, fn); offs.push(() => target.removeEventListener(ev, fn)); };

  // ONE persistent teardown: invalidate the lane + dispose the refresh
  // listener. Stays registered until navigation; operations never unregister
  // it (each op registers its OWN separate abort callback below).
  ctx.onTeardown(() => {
    const errors = [];
    try { roomsGen.bump(); } catch (e) { errors.push(e); }
    for (const off of offs) { try { off(); } catch (e) { errors.push(e); } }
    if (errors.length) return { ok: false, errors };
    return undefined;
  });

  const setStatus = (text, cls = '') => {
    if (statusEl) { statusEl.textContent = text; statusEl.className = `admin-status${cls ? ' ' + cls : ''}`; }
  };

  function renderTable(data) {
    const rooms = data.rooms;
    const parts = [`${rooms.length} room${rooms.length === 1 ? '' : 's'}`];
    if (data.summary.remote > 0) parts.push(`${data.summary.local} local, ${data.summary.remote} remote`);
    if (countEl) countEl.textContent = parts.join(' · ');

    if (capNoteEl) {
      const capped = rooms.length > ROW_CAP;
      capNoteEl.hidden = !capped;
      capNoteEl.textContent = capped ? `showing ${ROW_CAP} of ${rooms.length}` : '';
    }

    if (!rooms.length) {
      body.innerHTML = '<tr><td colspan="9" class="admin-loading">No active rooms.</td></tr>';
      return;
    }

    body.innerHTML = '';
    for (const r of rooms.slice(0, ROW_CAP)) {
      const row = document.createElement('tr');
      row.innerHTML = roomRowHtml(r);
      body.appendChild(row);
    }
  }

  // A malformed/failed response is ONE structured section error — the table
  // NEVER partially renders (rows already committed by a PRIOR successful load
  // are wiped too, since the section as a whole is now unverified).
  function renderSectionError(err) {
    const message = describeError(err);
    body.innerHTML = `<tr><td colspan="9" class="admin-loading">Error: ${escapeHtml(message)}</td></tr>`;
    if (countEl) countEl.textContent = '–';
    if (capNoteEl) { capNoteEl.hidden = true; capNoteEl.textContent = ''; }
    setStatus(`Error: ${message}`, 'error');
  }

  // Shared by the initial load AND every refresh click — see the header
  // comment for the shared-generation ordering contract.
  async function load() {
    const token = roomsGen.bump();
    const op = createOperation();
    const unregister = ctx.onTeardown(() => op.abort(navAbort()));
    let abortWait = null;
    if (refreshBtn) refreshBtn.disabled = true;
    body.innerHTML = '<tr><td colspan="9" class="admin-loading">Loading rooms&hellip;</td></tr>';
    setStatus('', '');
    let outcome;
    try {
      abortWait = whenAborted(op.signal);
      const resp = await Promise.race([api.get(EP, { signal: op.signal }), abortWait.promise]);
      // A successful response commit requires the CURRENT token + !op.closed.
      if (roomsGen.isStale(token)) { outcome = { ok: false, error: { kind: 'cancelled', reason: 'stale', endpoint: EP, status: null, cause: null } }; return outcome; }
      if (op.closed || op.signal.aborted) { outcome = toError(EP, op.signal.reason ?? abortErr()); return outcome; }
      const v = validateShape(EP, resp);
      if (!v.ok) { renderSectionError(v.error); outcome = v; return outcome; }
      renderTable(resp);
      outcome = { ok: true };
    } catch (err) {
      // Terminal timeout/error presentation checks ONLY the lane token (never
      // op.closed, which a deadline abort has already flipped true) — so the
      // CURRENT screen still recovers after a timeout; a navigated-away screen
      // (roomsGen already bumped) suppresses this same write.
      if (roomsGen.isStale(token)) { outcome = { ok: false, error: { kind: 'cancelled', reason: 'stale', endpoint: EP, status: null, cause: null } }; return outcome; }
      const mapped = toError(EP, err);
      renderSectionError(mapped.error);
      outcome = mapped;
    } finally {
      if (abortWait) abortWait.cancel();
      op.finish();
      unregister();
      // Refresh-button baseline restore: LANE TOKEN only, matching the
      // terminal presentation above (recovers same-screen, suppressed after
      // navigation).
      if (refreshBtn && !roomsGen.isStale(token)) refreshBtn.disabled = false;
    }
    return outcome;
  }

  if (refreshBtn) on(refreshBtn, 'click', () => { load(); });

  return load();
}
